import PropTypes from 'prop-types';

import QuantityButton from 'honeydew/QuantityButton';

import { PRODUCT_DATA_PROP_TYPE, PRODUCT_META_DATA_PROP_TYPE } from './types';
import useQuantityStepper from './useQuantityStepper';

/**
 * @param {Object} props
 * @param {boolean} props.isPerk - Whether the product is a perk item that can be redeemed with points
 * @param {boolean} props.isPerkRedeemable - Whether the perk item is currently redeemable by the user
 * @param {import('./types').ProductMetaData} props.metaData
 * @param {number|null} props.pointPrice - The number of points required to redeem this perk item , or null if not a perk item. This value is Plus Membership aware.
 * @param {import('./types').ProductData} props.productData
 * @param {Object} props.productSelectionData
 * @param {Function} props.setModalOpen
 * @param {boolean} props.wineIneligible
 */
function QuantityStepper({
    dsSource,
    isPerk,
    isPerkRedeemable,
    metaData,
    pointPrice,
    productData,
    productSelectionData,
    setModalOpen,
    wineIneligible,
}) {
    /*
        TODO: verify v1/v2 parity for these values

        aisleID,
        carouselID,
        carouselName,
        carouselHorizontalScroll: carouselPagination,
        customAisle,
        dsSource,
        isPerk,
        pointPrice,
        productID: id,
        productName: name,
        searchRecommendation,
        isCarouselModalCard,
     */

    const {
        decrementProductQty,
        incrementProductQty,
        isLoading: updateProductQtyLoading,
    } = useQuantityStepper({
        dsSource: dsSource ?? productData?.dsSource,
        isPerk,
        pointPrice,
        productID: productData?.id,
        productName: productData?.name,
        plusMemberPriceEligible: productData?.plusMemberPriceEligible,
        ...metaData,
    });

    /**
     * Determines the analytics event type for product add clicks based on the context.
     * The event type varies depending on whether the product card appears in a carousel,
     * modal, or standard view, and whether it's in an aisle or weekly picks section.
     *
     * @returns {string} The event type string to use for analytics tracking
     * - 'product-card' for standard product cards
     * - 'product-card-aisle-carousel-modal' for carousel modal cards in aisles
     * - 'product-card-weekly-picks-carousel-modal' for carousel modal cards in weekly picks
     * - 'product-card-aisle-carousel' for carousel cards in aisles
     * - 'product-card-weekly-picks-carousel' for carousel cards in weekly picks
     */
    const getAddClickEventType = () => {
        let eventType = 'product-card';
        const pageName = metaData?.aisleID ? `-aisle` : '-weekly-picks';

        if (metaData?.isCarouselModalCard) {
            eventType = `product-card${pageName}-carousel-modal`;
        } else if (metaData?.carouselID) {
            eventType = `product-card${pageName}-carousel`;
        }

        return eventType;
    };

    /**
     * Handles clicks on the add quantity button.
     * Prevents event bubbling, checks if the user is eligible to purchase alcohol products,
     * and either shows an age verification modal or increments the product quantity.
     *
     * @param {React.MouseEvent} e - The click event object
     */
    const handleAddClick = (e) => {
        e.stopPropagation();

        if (wineIneligible && (productData?.containsDrinksItem || productData?.isDrinksItem)) {
            setModalOpen(true);
            return;
        }

        const eventType = getAddClickEventType();
        incrementProductQty(eventType);
    };

    /**
     * Handles keyboard events on the quantity stepper.
     * Prevents event bubbling when Enter key is pressed to avoid triggering
     * parent element click handlers, like navigating to the product detail page.
     *
     * @param {React.KeyboardEvent} e - The keyboard event object
     */
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.code === 'Enter') {
            e.stopPropagation();
        }
    };

    /**
     * Handles clicks on the remove quantity button.
     * Prevents event bubbling and decrements the product quantity.
     *
     * @param {React.MouseEvent} e - The click event object
     */
    const handleRemoveClick = (e) => {
        e.stopPropagation();
        decrementProductQty('product-card');
    };

    const isDisabled =
        updateProductQtyLoading ||
        (isPerk && !isPerkRedeemable && !productSelectionData?.qty) ||
        (productData.soldOut && !productSelectionData?.qty);

    return (
        <QuantityButton
            disabled={isDisabled}
            maxQuantity={productData?.soldOut ? productSelectionData?.qty : productData?.maxQuantity}
            onAdd={handleAddClick}
            onKeyDown={handleKeyDown}
            onRemove={handleRemoveClick}
            quantity={productSelectionData?.qty ?? 0}
            variant="primary"
            size="md"
            fullWidth={false}
            points={isPerk ? pointPrice : null}
            removeAdd={isPerk && !isPerkRedeemable}
            renderAddText={isPerk}
        />
    );
}

QuantityStepper.propTypes = {
    dsSource: PropTypes.string,
    isPerk: PropTypes.bool,
    isPerkRedeemable: PropTypes.bool,
    metaData: PRODUCT_META_DATA_PROP_TYPE,
    pointPrice: PropTypes.number,
    productData: PRODUCT_DATA_PROP_TYPE,
    productSelectionData: PropTypes.shape({
        qty: PropTypes.number,
    }),
    setModalOpen: PropTypes.func,
    wineIneligible: PropTypes.bool,
};

QuantityStepper.defaultProps = {
    dsSource: null,
    isPerk: false,
    isPerkRedeemable: false,
    metaData: {},
    pointPrice: null,
    productData: {},
    productSelectionData: {},
    setModalOpen: () => {},
    wineIneligible: false,
};

export default QuantityStepper;
